<template>
  <div class="mt-4">
    <v-card flat class="content-container">
      <v-card-text v-if="loadingProfile">
        <v-layout class="ma-4">
          <v-flex xs12>
            <v-progress-circular indeterminate size="40" color="primary" />
          </v-flex>
        </v-layout>
      </v-card-text>
      <template v-else>
        <v-card-text>
          <!-- Info -->
          <v-layout column class="mb-4">
            <v-layout
              align-center
              :justify-center="$vuetify.breakpoint.mdAndUp"
              :justify-left="$vuetify.breakpoint.smAndDown"
            >
              <v-avatar :tile="true" color="grey">
                <v-icon color="white"> person </v-icon>
              </v-avatar>
              <div class="ml-2 ph-no-capture">
                <v-layout align-center>
                  {{ currentUser.full_name }}
                  <v-tooltip bottom class="ml-1">
                    <v-icon slot="activator" size="15"> info </v-icon>
                    <span>{{ $t('profile.nameChangeMessage') }}</span>
                  </v-tooltip>
                </v-layout>
              </div>
            </v-layout>
          </v-layout>

          <!-- Settings: Basics -->
          <v-layout wrap class="aligned mb-4">
            <v-flex xs12 sm4 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div class="aligned">
                {{ $t('profile.primaryContactEmail') }}
              </div>
              <v-text-field
                solo
                :placeholder="$t('profile.email')"
                v-model="currentUser.email"
                class="default-input ph-no-capture"
                :reverse="rightToLeft"
                :rules="rules.email"
              />
            </v-flex>
            <v-flex
              xs12
              sm4
              :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }"
              v-if="isIndividual"
            >
              <div class="aligned">
                {{ $t('profile.workEmail') }}
              </div>
              <v-text-field
                solo
                :placeholder="$t('profile.workEmail')"
                v-model="newCompany.business_email"
                class="default-input ph-no-capture"
                :reverse="rightToLeft"
              />
            </v-flex>
            <v-flex xs12 sm4 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div class="aligned">
                {{ $t('profile.contactMethod') }}
              </div>
              <v-select
                v-model="currentUser.preferred_contact_method"
                solo
                :items="preferredContactMethods"
                placeholder="Please select a contact method"
                item-text="label"
                item-value="value"
                class="default-input mb-1"
              />
            </v-flex>
            <v-flex xs12 sm4 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div class="aligned">
                {{ $t('profile.phoneNumber') }}
              </div>
              <v-text-field
                solo
                v-model="currentUser.phone_number"
                class="default-input ph-no-capture"
                label="(777) 777- 7777"
                mask="phone"
                :reverse="rightToLeft"
              />
            </v-flex>
            <v-flex xs12 sm4 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div class="aligned">
                {{ $t('profile.workPhoneNumber') }}
              </div>
              <v-text-field
                solo
                v-model="currentUser.work_phone_number"
                class="default-input ph-no-capture"
                label="Optional"
                mask="phone"
                :reverse="rightToLeft"
              />
            </v-flex>
            <v-flex xs12 sm4 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div class="aligned">
                {{ $t('profile.workPhoneExtension') }}
              </div>
              <v-text-field
                solo
                v-model="currentUser.work_phone_extension"
                class="default-input"
                label="Optional"
                :reverse="rightToLeft"
              />
            </v-flex>
            <v-flex
              xs12
              sm4
              :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }"
              v-if="!isIndividual"
            >
              <div class="aligned">
                {{ $t('profile.timezone') }}
              </div>
              <v-select
                v-model="currentUser.time_zone"
                solo
                :items="timeZones"
                class="default-input mb-1"
              />
            </v-flex>
            <v-flex xs12 sm4 class="px-2 text-xs-left">
              <div>
                {{ $t('profile.2fa') }}
              </div>
              <v-switch
                class="default-input ma-0 pa-0"
                v-model="currentUser.enabled_2fa"
                :disabled="force2FAOnUsers"
                :true-value="true"
                :false-value="false"
                label="2FA"
              />
              <div class="profile-normalize-switch-margin ph-no-capture">
                <sx-tiny color="gray">
                  {{
                    $t('profile.2faDescription').replace(
                      'EMAIL',
                      currentUser.email,
                    )
                  }}
                </sx-tiny>
              </div>
            </v-flex>
            <v-flex
              xs12
              sm4
              :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }"
              v-if="newCompany.entity_type === 'Individual'"
            >
              <div class="aligned">{{ $t('profile.timezone') }}</div>
              <v-select
                v-model="currentUser.time_zone"
                solo
                :items="timeZones"
                class="default-input mb-1"
              />
            </v-flex>
            <v-flex xs12 sm4 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div class="aligned">
                {{ $t('profile.language') }}
              </div>
              <SwitchLocale
                v-model="currentUser.language"
                :refresh-needed.sync="refreshNeeded"
              />
            </v-flex>
          </v-layout>

          <v-divider
            v-if="newCompany.entity_type !== 'Individual'"
            class="my-4"
          />

          <!-- Settings Partial: Non-individual -->
          <v-layout column class="mb-4" v-if="!isIndividual">
            <v-layout align-center justify-center>
              <v-avatar class="primary">
                <v-icon color="white"> business </v-icon>
              </v-avatar>
              <div class="ml-2">
                <v-layout align-center>
                  {{ newCompany.name }}
                  <v-tooltip bottom class="ml-1">
                    <v-icon slot="activator" size="15"> info </v-icon>
                    <span>{{ $t('profile.businessNameChangeMessage') }}</span>
                  </v-tooltip>
                </v-layout>
              </div>
            </v-layout>
          </v-layout>

          <!-- Settings Partial: Non-individual -->
          <v-layout wrap class="aligned" v-if="!isIndividual">
            <v-flex xs12 sm6 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div class="aligned">
                {{ $t('profile.businessEmail') }}
              </div>
              <v-text-field
                solo
                :placeholder="$t('profile.businessEmail')"
                v-model="newCompany.business_email"
                class="default-input ph-no-capture"
                :reverse="rightToLeft"
              />
            </v-flex>
            <v-flex xs12 sm6 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div>
                {{ $t('profile.otherEmail') }}
              </div>
              <v-text-field
                :placeholder="$t('profile.otherEmail')"
                v-model="newCompany.other_email"
                solo
                class="default-input ph-no-capture"
                :reverse="rightToLeft"
              />
            </v-flex>
            <v-flex xs12 sm12 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div class="aligned">
                {{ $t('profile.streetAddress') }}
              </div>
              <v-text-field
                solo
                :placeholder="$t('profile.streetAddress')"
                v-model="newCompany.street"
                class="default-input ph-no-capture"
                :reverse="rightToLeft"
              />
            </v-flex>
            <v-flex xs12 sm6 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div>
                {{ $t('profile.city') }}
              </div>
              <v-text-field
                solo
                :placeholder="$t('profile.city')"
                v-model="newCompany.city"
                class="default-input ph-no-capture"
              />
            </v-flex>
            <v-flex xs12 sm6 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div>
                {{ $t('profile.state') }}
              </div>
              <v-autocomplete
                solo
                hide-selected
                :placeholder="$t('profile.state')"
                v-model="newCompany.state"
                class="default-input"
                :items="states"
              />
            </v-flex>
            <v-flex xs12 sm6 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div>
                {{ $t('profile.zipcode') }}
              </div>
              <v-text-field
                solo
                :placeholder="$t('profile.zipcode')"
                v-model="newCompany.zip"
                class="default-input"
                type="number"
                :reverse="rightToLeft"
              />
            </v-flex>
            <v-flex xs12 sm6 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div>{{ $t('profile.country') }}</div>
              <v-autocomplete
                solo
                :placeholder="$t('profile.country')"
                clearable
                :items="otherCountries"
                v-model="newCompany.country"
                @change="handleChangeCountry"
                class="default-input ph-no-capture"
              />
            </v-flex>
          </v-layout>

          <!-- Settings Partial: Individual -->
          <v-layout wrap class="aligned" v-else>
            <v-flex xs12 sm12 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div class="aligned">
                {{ $t('profile.streetAddress') }}
              </div>
              <v-text-field
                solo
                :placeholder="$t('profile.streetAddress')"
                v-model="newCompany.street"
                class="default-input ph-no-capture"
                :reverse="rightToLeft"
              />
            </v-flex>
            <v-flex xs12 sm6 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div>
                {{ $t('profile.city') }}
              </div>
              <v-text-field
                solo
                :placeholder="$t('profile.city')"
                v-model="newCompany.city"
                class="default-input ph-no-capture"
                :reverse="rightToLeft"
              />
            </v-flex>
            <v-flex xs12 sm6 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div>
                {{ $t('profile.state') }}
              </div>
              <v-autocomplete
                solo
                hide-selected
                :placeholder="$t('profile.state')"
                v-model="newCompany.state"
                class="default-input ph-no-capture"
                :items="states"
              />
            </v-flex>
            <v-flex xs12 sm6 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div>
                {{ $t('profile.zipcode') }}
              </div>
              <v-text-field
                solo
                :placeholder="$t('profile.zipcode')"
                v-model="newCompany.zip"
                class="default-input ph-no-capture"
                type="number"
                :reverse="rightToLeft"
              />
            </v-flex>
            <v-flex xs12 sm6 :class="{ 'px-2': $vuetify.breakpoint.mdAndUp }">
              <div>{{ $t('profile.country') }}</div>
              <v-autocomplete
                solo
                :placeholder="$t('profile.country')"
                clearable
                :items="otherCountries"
                v-model="newCompany.country"
                @change="handleChangeCountry"
                class="default-input ph-no-capture"
              />
            </v-flex>
          </v-layout>
        </v-card-text>

        <!-- Actions: Without isFirmCommunicationDisabled -->
        <v-card-actions v-if="!isFirmCommunicationDisabled" class="px-4">
          <v-switch
            class="default-input ma-0 pa-0"
            v-model="currentUserPreferences.hide_conversation_in_email"
            :true-value="true"
            :false-value="false"
            solo
            hide-details
            :label="$t('profile.hideConversationContentFromEmail')"
          />
        </v-card-actions>

        <!-- Actions: Password as preferred_sign_in_method -->
        <v-card-actions class="pb-4 px-4">
          <v-btn
            :block="$vuetify.breakpoint.smAndDown"
            outline
            class="primary--text text-capitalize px-2 mr-2"
            @click="resetPassword"
            v-if="currentUser.preferred_sign_in_method === 'password'"
          >
            {{ $t('profile.resetPassword') }}
          </v-btn>

          <v-spacer class="hidden-xs-only" />

          <v-btn
            :block="$vuetify.breakpoint.smAndDown"
            class="primary text-capitalize"
            @click="save"
          >
            <v-icon left> check </v-icon>
            {{ $t('profile.saveChanges') }}
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </div>
</template>

<script>
import { firebase } from '@firebase/app';
import '@firebase/auth';
import API from '@/api';
import { mapGetters } from 'vuex';
import get from 'lodash/get';
import {
  otherCountries,
  timeZones,
  states,
  rules,
} from '@/constants/variables';
import SwitchLocale from '@/components/SwitchLanguage';
import UtilsMixins from '@/mixins/UtilsMixins';
import SxTiny from '@/components/sx/SxTiny';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';

export default {
  name: 'profile',

  props: {
    currentCompanyId: {
      type: Number,
    },
  },

  mixins: [UtilsMixins, FirmPreferenceMixins],

  data() {
    return {
      timeZones,
      states,
      rules,
      otherCountries,
      loadingProfile: false,
      preferredContactMethods: [
        {
          label: this.$t('profile.emailAndText'),
          value: 'sms',
        },
        {
          label: this.$t('profile.email'),
          value: 'email',
        },
      ],
      currentUserPreferences: {},
      newCompany: {},
      refreshNeeded: false,
    };
  },

  components: {
    SwitchLocale,
    SxTiny,
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      isAdvisor: 'isAdvisor',
      defaultCompany: 'defaultCompany',
      isFirmCommunicationDisabled: 'isFirmCommunicationDisabled',
    }),
    isIndividual() {
      return this.newCompany.entity_type === 'Individual';
    },
  },

  methods: {
    get,
    handleChangeCountry(value) {
      if (!value) {
        this.newCompany.country = null;
      }
    },
    async save(e) {
      e && e.preventDefault && e.preventDefault();

      if (
        this.currentUser.preferred_sign_in_method &&
        this.currentUser.preferred_sign_in_method === 'sms' &&
        this.currentUser.phone_number === ''
      ) {
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'Unable to remove phone number because your current sign in method is set to SMS Verification Code.',
        });
        return;
      }

      this.loadingProfile = true;

      const {
        name,
        street,
        state,
        city,
        zip,
        country,
        other_email,
        business_email,
      } = this.newCompany;
      const {
        email,
        phone_number,
        preferred_contact_method,
        time_zone,
        work_phone_number,
        work_phone_extension,
        language,
      } = this.currentUser;
      try {
        await API.user.updateUser({
          id: this.currentUser.id,
          enabled_2fa: this.currentUser.enabled_2fa,
        });

        await API.company.updateCompany({
          id: this.currentCompanyId,
          userId: this.currentUser.id,
          name,
          email,
          street,
          state,
          city,
          zip,
          country,
          phone_number,
          work_phone_number,
          work_phone_extension,
          preferred_contact_method,
          time_zone,
          other_email,
          business_email,
          language,
          preferences: this.currentUserPreferences,
        });
        await this.$store.dispatch('companies/getCompanies');
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Information has been saved successfully.',
        });
      } catch (e) {
        const isEmailTaken = Boolean(
          e.response &&
            e.response.data &&
            e.response.data.error &&
            e.response.data.error.includes('EMAIL_EXISTS'),
        );
        const errorMessage = isEmailTaken
          ? 'This email address is taken.'
          : 'Something went wrong while saving information.';

        this.$store.dispatch('application/setErrorNotification', {
          message: errorMessage,
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.$i18n.locale = this.currentUser.language;
        this.setDocumentLang(this.currentUser.language);
        this.setDocumentDirectionPerLocale(this.currentUser.language);
        this.$store.dispatch('getCurrentUser');
        this.loadingProfile = false;
        if (this.refreshNeeded) {
          this.$router.go();
        }
      }
    },

    async resetPassword() {
      const auth = firebase.auth();
      const email = this.currentUser.email;

      try {
        await auth.sendPasswordResetEmail(email);
        this.$store.dispatch('application/setSuccessNotification', {
          message: `Reset Email sent to ${email}`,
        });
      } catch (e) {
        console.log(e);
      }
    },
  },

  async created() {
    try {
      this.loadingProfile = true;
      const response = await API.company.getCompany(this.currentCompanyId);
      this.newCompany = response.data;
      this.currentUserPreferences = this.currentUser.preferences
        ? this.currentUser.preferences
        : {};
    } catch (e) {
      console.log(e);
    } finally {
      this.$nextTick(() => {
        this.loadingProfile = false;
      });
    }
  },
};
</script>
